/**
 * landing-content.ts — statisk innhold for forsiden (norsk + engelsk)
 *
 * Forsiden er offentlig, så innholdet kan være statisk i frontend. Ingen
 * dynamiske verdier (tall, brukere, status fra systemet) skal legges her —
 * kun beskrivelser av produktet. Strukturen følger LandingContent
 * (src/types/landing.ts), slik at innholdet kan byttes mot backend senere.
 *
 * Lenker: anker (#…) innad på siden og /login, som er innloggingsruten.
 */

import type { LandingContent, Locale } from '@/types/landing';

const HERO_LIGHT = '/landing/dashboard-light.png';
const HERO_DARK = '/landing/dashboard-dark.png';

const no: LandingContent = {
  ui: {
    skipToContent: 'Hopp til innhold',
    navLabel: 'Hovednavigasjon',
    languageLabel: 'Språk',
    themeLabel: 'Tema',
    themeToDark: 'Bytt til mørkt tema',
    themeToLight: 'Bytt til lyst tema',
    login: 'Logg inn',
  },
  nav: [
    { label: 'Plattform', href: '#plattform' },
    { label: 'Moduler', href: '#moduler' },
    { label: 'Funksjoner', href: '#funksjoner' },
    { label: 'Webhotell', href: '/webhotell' },
  ],
  hero: {
    heading: 'inSuite',
    subheading:
      'Plattformen som samler jobber, utleie, annonsering, kunder og tilgangsstyring i én innlogging.',
    primaryCta: { label: 'Logg inn', href: '/login' },
    secondaryCta: { label: 'Se modulene', href: '#moduler' },
    imageLightSrc: HERO_LIGHT,
    imageDarkSrc: HERO_DARK,
    imageAlt: 'Dashboard i inSuite med nøkkeltall, grafer og tjenestestatus',
  },
  platform: {
    eyebrow: 'Plattform',
    heading: 'Én plattform. Flere moduler.',
    body: 'inSuite er bygget som en samling moduler som deler innlogging, roller og design. Hver modul løser sitt område, og du bytter mellom dem uten å logge inn på nytt.',
  },
  modules: {
    heading: 'Modulene i inSuite',
    intro: 'Velg modulen for å lese mer om hva den gjør.',
    items: [
      {
        id: 'nfiles',
        icon: 'files',
        name: 'nFiles',
        description: 'Filbehandling og jobber, med kjøretøy, forhandlere og typer.',
        status: 'Alpha',
        href: '#feature-nfiles',
      },
      {
        id: 'inrental',
        icon: 'rental',
        name: 'inRental',
        description: 'Utleie med bookingkalender og bookingliste.',
        href: '#feature-inrental',
      },
      {
        id: 'inads',
        icon: 'ads',
        name: 'inAds',
        description: 'Dashboard for Google Ads-annonsering.',
        href: '#feature-inads',
      },
      {
        id: 'crm',
        icon: 'crm',
        name: 'CRM',
        description: 'Selskaper, personer og kunder på ett sted.',
        href: '#feature-crm',
      },
      {
        id: 'admin',
        icon: 'admin',
        name: 'Administrasjon',
        description: 'Brukere, roller og tenants.',
        href: '#feature-admin',
      },
      {
        id: 'ai',
        icon: 'ai',
        name: 'AI-assistent',
        description: 'Chat som er tilgjengelig i hele plattformen.',
        href: '#funksjoner',
      },
    ],
  },
  features: [
    {
      id: 'nfiles',
      eyebrow: 'nFiles',
      heading: 'Jobber og filer, samlet',
      body: 'nFiles holder orden på jobbene dine med filer, samtaler og kjøretøy knyttet til hver jobb. Modulen er i alpha og brukes kun internt for testing.',
      includesLabel: 'Dette finner du',
      includes: ['Jobber med filer og chat', 'Kjøretøy', 'Forhandlere', 'Typer og statuser'],
      status: 'Alpha',
    },
    {
      id: 'inrental',
      eyebrow: 'inRental',
      heading: 'Full oversikt over bookinger',
      body: 'inRental gir deg bookinger både som kalender og som liste, slik at du ser hva som er ledig og hva som er opptatt.',
      includesLabel: 'Dette finner du',
      includes: ['Bookingkalender', 'Bookingliste', 'Oversikt per kjøretøy'],
    },
    {
      id: 'inads',
      eyebrow: 'inAds',
      heading: 'Annonseresultater i dashbordet',
      body: 'inAds henter data fra Google Ads og viser dem i et dashboard med valgbar periode.',
      includesLabel: 'Dette finner du',
      includes: ['Google Ads-dashboard', 'Valgbar periode'],
    },
    {
      id: 'crm',
      eyebrow: 'CRM',
      heading: 'Kundene dine på ett sted',
      body: 'CRM samler selskaper, personer og kunder, med egen profilside for hver.',
      includesLabel: 'Dette finner du',
      includes: ['Selskaper', 'Personer', 'Kunder'],
    },
    {
      id: 'admin',
      eyebrow: 'Administrasjon',
      heading: 'Kontroll over hvem som får tilgang',
      body: 'Administrasjon lar deg styre brukere, roller og tenants. Menyer og sider tilpasses brukerens rolle.',
      includesLabel: 'Dette finner du',
      includes: ['Brukere', 'Roller og tillatelser', 'Tenants'],
    },
  ],
  capabilities: {
    heading: 'Felles for hele plattformen',
    items: [
      {
        icon: 'access',
        title: 'Rollebasert tilgang',
        body: 'Brukerne ser kun det rollen deres gir tilgang til.',
      },
      {
        icon: 'tenant',
        title: 'Flere tenants',
        body: 'Plattformen er bygget for å håndtere flere tenants.',
      },
      {
        icon: 'ai',
        title: 'AI-assistent',
        body: 'Chat-assistent som du åpner fra hvilken som helst side.',
      },
      {
        icon: 'realtime',
        title: 'Varsler i sanntid',
        body: 'Hendelser dukker opp som varsler mens du jobber.',
      },
      {
        icon: 'theme',
        title: 'Lyst og mørkt tema',
        body: 'Velg tema selv, eller følg innstillingen på enheten din.',
      },
    ],
  },
  webhotellPromo: {
    title: 'Webhotell fra inSuite',
    body: 'Drift, sikkerhet og support for nettsider, e-post og databaser, i pakker du kan bytte mellom.',
    cta: { label: 'Se webhotell', href: '/webhotell' },
  },
  cta: {
    heading: 'Klar til å komme i gang?',
    body: 'Logg inn for å åpne inSuite.',
    primary: { label: 'Logg inn', href: '/login' },
  },
  footer: {
    tagline: 'inSuite — jobber, utleie, annonsering og kunder i én plattform.',
    legal: 'inSuite',
    platformLabel: 'Plattform',
    modulesLabel: 'Moduler',
  },
};

const en: LandingContent = {
  ui: {
    skipToContent: 'Skip to content',
    navLabel: 'Main navigation',
    languageLabel: 'Language',
    themeLabel: 'Theme',
    themeToDark: 'Switch to dark theme',
    themeToLight: 'Switch to light theme',
    login: 'Log in',
  },
  nav: [
    { label: 'Platform', href: '#plattform' },
    { label: 'Modules', href: '#moduler' },
    { label: 'Capabilities', href: '#funksjoner' },
    { label: 'Web hosting', href: '/webhotell' },
  ],
  hero: {
    heading: 'inSuite',
    subheading:
      'The platform that brings jobs, rentals, advertising, customers and access control together under one login.',
    primaryCta: { label: 'Log in', href: '/login' },
    secondaryCta: { label: 'See the modules', href: '#moduler' },
    imageLightSrc: HERO_LIGHT,
    imageDarkSrc: HERO_DARK,
    imageAlt: 'inSuite dashboard with key figures, charts and service status',
  },
  platform: {
    eyebrow: 'Platform',
    heading: 'One platform. Many modules.',
    body: 'inSuite is a collection of modules that share login, roles and design. Each module covers its own area, and you move between them without signing in again.',
  },
  modules: {
    heading: 'The modules in inSuite',
    intro: 'Pick a module to read more about what it does.',
    items: [
      {
        id: 'nfiles',
        icon: 'files',
        name: 'nFiles',
        description: 'File handling and jobs, with vehicles, dealers and types.',
        status: 'Alpha',
        href: '#feature-nfiles',
      },
      {
        id: 'inrental',
        icon: 'rental',
        name: 'inRental',
        description: 'Rentals with a booking calendar and a booking list.',
        href: '#feature-inrental',
      },
      {
        id: 'inads',
        icon: 'ads',
        name: 'inAds',
        description: 'Dashboard for Google Ads advertising.',
        href: '#feature-inads',
      },
      {
        id: 'crm',
        icon: 'crm',
        name: 'CRM',
        description: 'Companies, persons and customers in one place.',
        href: '#feature-crm',
      },
      {
        id: 'admin',
        icon: 'admin',
        name: 'Administration',
        description: 'Users, roles and tenants.',
        href: '#feature-admin',
      },
      {
        id: 'ai',
        icon: 'ai',
        name: 'AI assistant',
        description: 'Chat available across the whole platform.',
        href: '#funksjoner',
      },
    ],
  },
  features: [
    {
      id: 'nfiles',
      eyebrow: 'nFiles',
      heading: 'Jobs and files, together',
      body: 'nFiles keeps your jobs organised, with files, conversations and vehicles tied to each job. The module is in alpha and is used internally for testing only.',
      includesLabel: 'What you will find',
      includes: ['Jobs with files and chat', 'Vehicles', 'Dealers', 'Types and statuses'],
      status: 'Alpha',
    },
    {
      id: 'inrental',
      eyebrow: 'inRental',
      heading: 'Full overview of bookings',
      body: 'inRental shows bookings both as a calendar and as a list, so you can see what is available and what is taken.',
      includesLabel: 'What you will find',
      includes: ['Booking calendar', 'Booking list', 'Overview per vehicle'],
    },
    {
      id: 'inads',
      eyebrow: 'inAds',
      heading: 'Ad results in your dashboard',
      body: 'inAds pulls data from Google Ads and shows it in a dashboard with a selectable period.',
      includesLabel: 'What you will find',
      includes: ['Google Ads dashboard', 'Selectable period'],
    },
    {
      id: 'crm',
      eyebrow: 'CRM',
      heading: 'Your customers in one place',
      body: 'CRM brings companies, persons and customers together, with a profile page for each.',
      includesLabel: 'What you will find',
      includes: ['Companies', 'Persons', 'Customers'],
    },
    {
      id: 'admin',
      eyebrow: 'Administration',
      heading: 'Control over who gets access',
      body: 'Administration lets you manage users, roles and tenants. Menus and pages adapt to the user’s role.',
      includesLabel: 'What you will find',
      includes: ['Users', 'Roles and permissions', 'Tenants'],
    },
  ],
  capabilities: {
    heading: 'Shared across the platform',
    items: [
      {
        icon: 'access',
        title: 'Role-based access',
        body: 'Users only see what their role gives them access to.',
      },
      {
        icon: 'tenant',
        title: 'Multiple tenants',
        body: 'The platform is built to handle multiple tenants.',
      },
      {
        icon: 'ai',
        title: 'AI assistant',
        body: 'A chat assistant you can open from any page.',
      },
      {
        icon: 'realtime',
        title: 'Real-time notifications',
        body: 'Events show up as notifications while you work.',
      },
      {
        icon: 'theme',
        title: 'Light and dark theme',
        body: 'Pick a theme yourself, or follow your device setting.',
      },
    ],
  },
  webhotellPromo: {
    title: 'Web hosting from inSuite',
    body: 'Operations, security and support for websites, email and databases, in plans you can move between.',
    cta: { label: 'See web hosting', href: '/webhotell' },
  },
  cta: {
    heading: 'Ready to get started?',
    body: 'Log in to open inSuite.',
    primary: { label: 'Log in', href: '/login' },
  },
  footer: {
    tagline: 'inSuite — jobs, rentals, advertising and customers in one platform.',
    legal: 'inSuite',
    platformLabel: 'Platform',
    modulesLabel: 'Modules',
  },
};

export const LANDING_CONTENT: Record<Locale, LandingContent> = { no, en };
